'use client'

import {
  PLAYBOOK_CHECKLIST_STORAGE_KEY,
  getChecklistItem,
  setChecklistItem,
} from '@/lib/pre-season-playbook/checklist-storage'
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import ColorScheme from '@/components/colorScheme'
import ArrowRight from '@/components/ArrowRight'
import type { ColorScheme as ColorSchemeType } from '@/sanity.types'
import { renderInlineMarkdown } from '@/lib/inline-markdown'

const sunOutlineClass =
  'is-outline relative z-0 whitespace-nowrap bg-transparent before:content-[\'\'] before:-z-1 before:absolute before:border-[0.26em] before:border-(--color-accent) before:block before:rounded-[50%] before:inset-[-0.22em_-.45em]'

type StepMarker = 'numbered' | 'checkbox' | 'bullet'

type Props = {
  heading: string
  body: string
  index?: number
  markerColorScheme?: ColorSchemeType
  checkboxBorderClassName?: string
  checkboxCheckClassName?: string
  stepMarker?: StepMarker
  isLastInStep?: boolean
  highlightHeading?: boolean
  headingClassName?: string
  bodyClassName?: string
  /** Stable id for persisting checkbox state in localStorage. */
  storageKey?: string
  /** localStorage bucket for this checklist. Defaults to the pre-season playbook. */
  checklistStorageKey?: string
  children?: ReactNode
}

const strikeTextClass =
  'text-greyscale-gravel line-through decoration-solid decoration-[0.07em] box-decoration-clone [-webkit-box-decoration-break:clone]'

function ItemHeading({
  heading,
  className,
  highlightHeading = false,
  strike = false,
}: {
  heading: string
  className: string
  highlightHeading?: boolean
  strike?: boolean
}) {
  const headingText = strike ? (
    <span className={strikeTextClass}>{heading}</span>
  ) : (
    heading
  )

  if (!highlightHeading) {
    return <p className={className}>{headingText}</p>
  }

  return (
    <p className={className}>
      <span
        className={sunOutlineClass}
        style={{ '--color-accent': 'var(--color-sun)' } as CSSProperties}
      >
        {heading}
      </span>
    </p>
  )
}

function PlaybookStepCheckbox({
  label,
  colorScheme,
  checkboxBorderClassName,
  checkboxCheckClassName,
  checked,
  onCheckedChange,
}: {
  label: string
  colorScheme: ColorSchemeType
  checkboxBorderClassName?: string
  checkboxCheckClassName?: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  const borderClass =
    checkboxBorderClassName ?? 'border-(--color-background)'
  const checkClass = checkboxCheckClassName ?? 'text-(--color-foreground)'

  return (
    <ColorScheme colorScheme={colorScheme}>
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={`Mark "${label}" complete`}
        onClick={() => onCheckedChange(!checked)}
        className={`absolute left-0 top-0.5 z-10 flex size-6 shrink-0 cursor-pointer items-center justify-center rounded border-[3px] bg-transparent p-0 ${borderClass}`}
      >
        {checked && (
          <svg
            aria-hidden="true"
            viewBox="0 0 16 16"
            className={`size-5 ${checkClass}`}
            fill="none"
          >
            <path
              d="M3.5 8.5 6.5 11.5 12.5 4.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    </ColorScheme>
  )
}

export default function PlaybookItem({
  heading,
  body,
  index,
  markerColorScheme,
  checkboxBorderClassName,
  checkboxCheckClassName,
  stepMarker = 'numbered',
  isLastInStep = false,
  highlightHeading = false,
  headingClassName = 'font-semibold mb-1',
  bodyClassName = 'opacity-80',
  storageKey,
  checklistStorageKey = PLAYBOOK_CHECKLIST_STORAGE_KEY,
  children,
}: Props) {
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    if (!storageKey) return
    setChecked(getChecklistItem(storageKey, checklistStorageKey))
  }, [storageKey, checklistStorageKey])

  const handleCheckedChange = (nextChecked: boolean) => {
    setChecked(nextChecked)
    if (storageKey) {
      setChecklistItem(storageKey, nextChecked, checklistStorageKey)
    }
  }

  const isChecked = stepMarker === 'checkbox' && checked
  const useStepLayout =
    stepMarker === 'checkbox' ||
    stepMarker === 'bullet' ||
    (index != null && markerColorScheme)

  if (useStepLayout) {
    const isBullet = stepMarker === 'bullet'
    const markerSizeClass = stepMarker === 'checkbox' ? 'top-[26px]' : 'top-7 md:top-8'
    const lineLeftClass = stepMarker === 'checkbox' ? 'left-3' : 'left-3.5 md:left-4'
    const numberedHeading =
      stepMarker === 'checkbox' && index != null ? `${index}. ${heading}` : heading
    const paddingClass = stepMarker === 'checkbox' ? 'pl-9' : isBullet ? 'pl-7' : 'pl-10 md:pl-10'

    return (
      <div className={`relative ${paddingClass}`}>
        {stepMarker === 'numbered' && (!isLastInStep || children) && (
          <div
            aria-hidden="true"
            className={`absolute ${markerSizeClass} w-0.5 -translate-x-1/2 bg-sky ${lineLeftClass} ${
              isLastInStep ? 'bottom-0' : '-bottom-6 md:-bottom-7'
            }`}
          />
        )}

        {stepMarker === 'checkbox' ? (
          markerColorScheme && (
            <PlaybookStepCheckbox
              label={heading}
              colorScheme={markerColorScheme}
              checkboxBorderClassName={checkboxBorderClassName}
              checkboxCheckClassName={checkboxCheckClassName}
              checked={checked}
              onCheckedChange={handleCheckedChange}
            />
          )
        ) : isBullet ? (
          markerColorScheme && (
            <ColorScheme colorScheme={markerColorScheme}>
              <span
                aria-hidden="true"
                className="absolute left-0 top-[5px] inline-flex size-4 text-(--color-background)"
              >
                <ArrowRight className="size-4" />
              </span>
            </ColorScheme>
          )
        ) : (
          markerColorScheme && (
            <ColorScheme colorScheme={markerColorScheme}>
              <div
                aria-hidden="true"
                className="absolute left-0 top-0 z-10 flex size-7 shrink-0 items-center justify-center rounded-full bg-(--color-background) text-(--color-foreground) text-xs font-semibold tabular-nums md:size-8"
              >
                {index}
              </div>
            </ColorScheme>
          )
        )}

        <div>
          <ItemHeading
            heading={numberedHeading}
            className={headingClassName}
            highlightHeading={highlightHeading}
            strike={isChecked}
          />
          {body ? (
            <p className={isChecked ? `${bodyClassName} opacity-100` : bodyClassName}>
              <span className={isChecked ? strikeTextClass : undefined}>
                {renderInlineMarkdown(body)}
              </span>
            </p>
          ) : null}
          {children}
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-start gap-3 text-sm leading-[1.4]">
      {index != null && (
        <span className="shrink-0 font-semibold tabular-nums opacity-60" aria-hidden="true">
          {index}.
        </span>
      )}
      <div>
        <ItemHeading
          heading={heading}
          className={headingClassName}
          highlightHeading={highlightHeading}
        />
        {body ? (
          <p className={bodyClassName}>{renderInlineMarkdown(body)}</p>
        ) : null}
      </div>
    </div>
  )
}
